import { useState } from 'react';
import QrScanner from 'qr-scanner';
import useFileUploadApi from './useFileUploadApi';
import gql from 'not-graphql-tag';
import { useApolloClient } from '@apollo/react-hooks';
import {
  ExtractTextFromDocumentOutput,
  OcrDocumentType,
  OcrFieldNames,
  QueryExtractTextFromDocumentArgs,
} from '../../__generated__/pgeplus-types';
import { useLocation } from '@reach/router';

export const getTextExtractQuery = gql`
  query ExtractTextFromDocument($input: ExtractTextFromDocumentInput!) {
    extractTextFromDocument(input: $input) {
      textExtracts {
        fieldName
        value
      }
    }
  }
`;

export const useImageOcr = () => {
  const apolloClient = useApolloClient();
  const { getPgeStoreDownloadUrl } = useFileUploadApi();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { pathname } = useLocation();
  const isInstallerPortal = pathname.startsWith('/installer/');

  const getImageUrlFromKey = async (pgeStoreKey: string) => {
    const fileUrls = await getPgeStoreDownloadUrl({ keys: [pgeStoreKey] });
    const imageUrl = fileUrls?.[0]?.downloadUrl;
    return Boolean(imageUrl) ? imageUrl : undefined;
  };

  const extractText = async (
    fileKey: string,
    fieldNamesToExtract: OcrFieldNames[],
    documentType: OcrDocumentType,
  ) => {
    try {
      setIsLoading(true);
      const result = await apolloClient.query<
        { extractTextFromDocument: ExtractTextFromDocumentOutput },
        QueryExtractTextFromDocumentArgs
      >({
        query: getTextExtractQuery,
        variables: {
          input: {
            documentType,
            fieldNames: fieldNamesToExtract,
            fileKey,
          },
        },
        context: isInstallerPortal
          ? {
              installerPortal: true,
              pgePlus: true,
            }
          : {
              pgePlus: true,
            },
        fetchPolicy: 'network-only',
      });

      return result?.data?.extractTextFromDocument;
    } catch (e) {
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const scanQrCode = async (pgeStoreKey: string) => {
    try {
      const imageUrl = await getImageUrlFromKey(pgeStoreKey);
      if (!imageUrl) {
        return undefined;
      }
      const imageBlob = await fetch(imageUrl).then(res => res.blob());
      return await QrScanner.scanImage(imageBlob, {}).catch((error: any) =>
        console.log(error || 'No QR code found.'),
      );
    } catch (e) {
      console.log(e);
      return undefined;
    }
  };

  return {
    scanQrCode,
    extractText,
    isLoading,
  };
};
